import type { ReactNode } from 'react';
import { PageContainer } from '@noor/ui';

/**
 * The isolation boundary for legacy mode. Every legacy screen renders inside
 * this wrapper, which does three things:
 *
 * 1. Sets `data-legacy`, the selector the legacy token layer
 *    (`app/styles/legacy.css`) is scoped to. Nothing outside this subtree can
 *    see a `--lg-*` variable, so the legacy palette can never leak into the
 *    new design system — and vice versa.
 * 2. Pins the old panel's font and light-only surface via `.legacy-root`, so
 *    the app shell's theme (which may be dark) doesn't bleed through into a
 *    reproduction that only ever existed in light mode.
 * 3. Centres the content in the SAME `PageContainer` the Eats and Delivery
 *    screens use, so legacy pages line up with the rest of the app on a wide
 *    monitor instead of stretching edge to edge.
 *
 * Kept in `entities` rather than `widgets` because both the shell chrome and
 * the page bodies need it, and a widget may not be imported by another widget.
 */
export function LegacyRoot({
  children,
  className = 'overflow-y-auto',
  contained = true,
  containerVariant = 'scroll',
}: {
  children: ReactNode;
  /**
   * Overrides the default scroll behaviour. Screens that own their own scroll
   * region — the order-create form, whose panel scrolls over a fixed map —
   * pass `relative overflow-hidden` instead.
   */
  className?: string;
  /**
   * Whether to centre the content at the app's standard max width.
   *
   * `false` for genuinely full-bleed screens — order-create is a viewport-wide
   * map with a panel floating over it, so constraining it would letterbox the
   * map and detach the panel from the left edge it is measured against.
   */
  contained?: boolean;
  /**
   * Variant for the inner PageContainer: 'scroll' (default, flow content) or
   * 'fill' (fills shell height for tables that scroll internally).
   */
  containerVariant?: 'scroll' | 'fill';
}) {
  return (
    // `flex-1` + a scroll mode mirrors what `PageShell variant="scroll"` does
    // for the new screens. `PageShell` itself is not used: it also applies
    // design-system padding (`p-6 lg:p-8`), and the legacy pages carry the old
    // panel's own spacing, which is what the reproduction is measured against.
    // Only the WIDTH constraint is shared. The app shell is a fixed-height flex
    // column, so without an owned scroll region here a tall legacy page
    // overflows the shell instead of scrolling inside it.
    <div data-legacy className={`legacy-root w-full flex-1 ${className}`}>
      {contained ? (
        <PageContainer variant={containerVariant}>{children}</PageContainer>
      ) : (
        children
      )}
    </div>
  );
}
